import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { bannersService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
    HiOutlinePhotograph,
    HiOutlinePlus,
    HiOutlineX,
    HiOutlinePencil,
    HiOutlineTrash,
    HiOutlineCheckCircle,
    HiOutlineInformationCircle,
    HiOutlineLink
} from 'react-icons/hi';
import './EventsManage.css';

const MEDIA_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5001/api').replace(/\/api$/, '');

const BannersManage = () => {
    const [banners, setBanners] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingBanner, setEditingBanner] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    useAuth();
    
    const [formData, setFormData] = useState({
        title_es: '',
        title_en: '',
        subtitle_es: '',
        subtitle_en: '',
        image_url: null,
        link_url: '',
        display_order: 0,
        is_active: true
    });

    useEffect(() => {
        loadBanners();
    }, []);

    const loadBanners = async () => {
        try {
            const response = await bannersService.getAll();
            setBanners(response.data);
        } catch (error) {
            toast.error('Error al cargar banners');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleFileChange = (e) => {
        const { files } = e.target;
        if (files && files[0]) {
            setFormData(prev => ({
                ...prev,
                image_url: files[0]
            }));

            if (files[0].type.startsWith('image/')) {
                const reader = new FileReader();
                reader.onloadend = () => {
                    setImagePreview(reader.result);
                };
                reader.readAsDataURL(files[0]);
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const submitData = new FormData();
            
            Object.keys(formData).forEach(key => {
                if (key !== 'image_url' && formData[key] !== null && formData[key] !== '') {
                    submitData.append(key, formData[key]);
                }
            });

            if (formData.image_url && formData.image_url instanceof File) {
                submitData.append('image_url', formData.image_url);
            }

            if (editingBanner) {
                await bannersService.update(editingBanner.id, submitData);
                toast.success('Banner actualizado exitosamente');
            } else {
                await bannersService.create(submitData);
                toast.success('Banner creado exitosamente');
            }

            resetForm();
            loadBanners();
        } catch (error) {
            toast.error(error.response?.data?.error || 'Error al guardar banner');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (banner) => {
        setEditingBanner(banner);
        setFormData({
            title_es: banner.title_es || '',
            title_en: banner.title_en || '',
            subtitle_es: banner.subtitle_es || '',
            subtitle_en: banner.subtitle_en || '',
            image_url: null,
            link_url: banner.link_url || '',
            display_order: banner.display_order || 0,
            is_active: banner.is_active
        });
        setImagePreview(banner.image_url ? `${MEDIA_URL}${banner.image_url}` : null);
        setShowModal(true);
    };

    const handleDelete = async (id, title) => {
        if (window.confirm(`¿Estás seguro de eliminar el banner "${title}"?`)) {
            try {
                await bannersService.delete(id);
                toast.success('Banner eliminado exitosamente');
                loadBanners();
            } catch (error) {
                toast.error('Error al eliminar banner');
                console.error(error);
            }
        }
    };

    const resetForm = () => {
        setFormData({
            title_es: '',
            title_en: '',
            subtitle_es: '',
            subtitle_en: '',
            image_url: null,
            link_url: '',
            display_order: 0,
            is_active: true
        });
        setEditingBanner(null);
        setImagePreview(null);
        setShowModal(false);
    };

    if (loading && banners.length === 0) {
        return (
            <div className="content-manage">
                <div className="loading-state">
                    <div className="spinner"></div>
                    <p>Cargando banners...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="content-manage">
            <div className="content-header">
                <div className="header-info">
                    <h1>Gestión de Banners</h1>
                    <p>Administra los banners del carrusel principal</p>
                </div>
                <button className="btn btn-primary" onClick={() => setShowModal(true)}>
                    <HiOutlinePlus />
                    <span>Nuevo Banner</span>
                </button>
            </div>

            {banners.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-state-icon">
                        <HiOutlinePhotograph />
                    </div>
                    <h3>No hay banners</h3>
                    <p>Comienza creando tu primer banner para el carrusel</p>
                    <button className="btn btn-primary" onClick={() => setShowModal(true)}>
                        <HiOutlinePlus />
                        Crear Banner
                    </button>
                </div>
            ) : (
                <div className="table-container">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>Imagen</th>
                                <th>Título</th>
                                <th>Subtítulo</th>
                                <th>Orden</th>
                                <th>Estado</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {banners.map(banner => (
                                <tr key={banner.id}>
                                    <td data-label="Imagen">
                                        <div className="banner-image-preview">
                                            <img 
                                                src={`${MEDIA_URL}${banner.image_url}`} 
                                                alt={banner.title_es}
                                                style={{ width: '120px', height: '60px', objectFit: 'cover', borderRadius: '8px' }}
                                            />
                                        </div>
                                    </td>
                                    <td data-label="Título">
                                        <strong>{banner.title_es || 'Sin título'}</strong>
                                        {banner.title_en && <div className="text-muted" style={{ fontSize: '0.85em' }}>{banner.title_en}</div>}
                                    </td>
                                    <td data-label="Subtítulo">
                                        {banner.subtitle_es || '-'}
                                        {banner.subtitle_en && <div className="text-muted" style={{ fontSize: '0.85em' }}>{banner.subtitle_en}</div>}
                                    </td>
                                    <td data-label="Orden">
                                        <span className="badge badge-gray">{banner.display_order}</span>
                                    </td>
                                    <td data-label="Estado">
                                        {banner.is_active ? (
                                            <span className="status-badge status-active">Activo</span>
                                        ) : (
                                            <span className="status-badge status-inactive">Inactivo</span>
                                        )}
                                    </td>
                                    <td>
                                        <div className="table-actions">
                                            <button
                                                className="btn-icon btn-secondary"
                                                onClick={() => handleEdit(banner)}
                                                title="Editar banner"
                                            >
                                                <HiOutlinePencil />
                                            </button>
                                            <button
                                                className="btn-icon btn-danger"
                                                onClick={() => handleDelete(banner.id, banner.title_es)}
                                                title="Eliminar banner"
                                            >
                                                <HiOutlineTrash />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Modal para crear/editar banner */}
            {showModal && (
                <div className="modal-overlay" onClick={resetForm}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2 className="modal-title">
                                {editingBanner ? 'Editar Banner' : 'Nuevo Banner'}
                            </h2>
                            <button className="modal-close" onClick={resetForm}>
                                <HiOutlineX />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit}>
                            <div className="modal-body">
                                <div className="alert-info">
                                    <HiOutlineInformationCircle />
                                    <span>Los campos en inglés se traducirán automáticamente si se dejan vacíos</span>
                                </div>

                                <div className="grid grid-cols-2">
                                    <div className="form-group">
                                        <label className="form-label">Título (Español) *</label>
                                        <input
                                            type="text"
                                            name="title_es"
                                            className="form-control"
                                            value={formData.title_es}
                                            onChange={handleInputChange}
                                            placeholder="Ej: Bienvenidos a FENINE"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Título (Inglés)</label>
                                        <input
                                            type="text"
                                            name="title_en"
                                            className="form-control"
                                            value={formData.title_en}
                                            onChange={handleInputChange}
                                            placeholder="Auto-traducido si se deja vacío"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">Subtítulo (Español)</label>
                                        <input
                                            type="text"
                                            name="subtitle_es"
                                            className="form-control"
                                            value={formData.subtitle_es}
                                            onChange={handleInputChange}
                                            placeholder="Ej: Federación Nacional de Ingeniería"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Subtítulo (Inglés)</label>
                                        <input
                                            type="text"
                                            name="subtitle_en"
                                            className="form-control"
                                            value={formData.subtitle_en}
                                            onChange={handleInputChange}
                                            placeholder="Auto-traducido si se deja vacío"
                                        />
                                    </div>

                                    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                                        <label className="form-label">
                                            <HiOutlinePhotograph style={{ marginRight: '8px' }} />
                                            Imagen del Banner * {editingBanner && '(Dejar vacío para mantener actual)'}
                                        </label>
                                        <input
                                            type="file"
                                            name="image_url"
                                            className="form-control"
                                            onChange={handleFileChange}
                                            accept="image/*"
                                            required={!editingBanner}
                                        />
                                        {imagePreview && (
                                            <div className="image-preview">
                                                <img src={imagePreview} alt="Preview" />
                                            </div>
                                        )}
                                    </div>

                                    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                                        <label className="form-label">
                                            <HiOutlineLink style={{ marginRight: '8px' }} />
                                            URL de Enlace (Opcional)
                                        </label>
                                        <input
                                            type="url"
                                            name="link_url"
                                            className="form-control"
                                            value={formData.link_url}
                                            onChange={handleInputChange}
                                            placeholder="https://ejemplo.com"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">Orden de Visualización</label>
                                        <input
                                            type="number"
                                            name="display_order"
                                            className="form-control"
                                            value={formData.display_order}
                                            onChange={handleInputChange}
                                            placeholder="0"
                                            min="0"
                                        />
                                        <small style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                                            Menor número aparece primero
                                        </small>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-check">
                                            <input
                                                type="checkbox"
                                                name="is_active"
                                                className="form-check-input"
                                                checked={formData.is_active}
                                                onChange={handleInputChange}
                                            />
                                            <span>Banner activo</span>
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                                    Cancelar
                                </button>
                                <button type="submit" className="btn btn-primary" disabled={loading}>
                                    {loading ? (
                                        <>
                                            <div className="spinner" style={{ width: '16px', height: '16px', borderWidth: '2px' }}></div>
                                            <span>Guardando...</span>
                                        </>
                                    ) : (
                                        <>
                                            <HiOutlineCheckCircle />
                                            <span>{editingBanner ? 'Actualizar' : 'Crear Banner'}</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BannersManage;
